import React, { useEffect } from "react";
import { ResponsiveBar, ResponsiveBarCanvas } from '@nivo/bar'
import { Select } from "@blueprintjs/select";
import {
    Button
} from "@blueprintjs/core";
import { FilterQueryParser } from "react-filter-box";
import useStateWithUrlParam from "../../../hooks/useStateWithUrlParam";


import "./CustomizableChart.scss"

const COLORS = ['#e8c1a0', '#f47560', '#f1e15b', '#e8a838', '#61cdbb', '#97e3d5'];

function hash(str) {
    if(!str){
        return 0
    }
    var hash = 0,
        i, chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 1) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    if (hash < 0) {
        hash = hash * -1;
    }
    return hash;
}

const parser = new FilterQueryParser();
function addClause(query, clause) {
    let trial_query = query.trim()
    while (parser.parse(trial_query).isError) {
        trial_query = trial_query.substring(0, trial_query.lastIndexOf(" "))
    }
    if (trial_query.length === 0) {
        return clause
    }
    return `${trial_query} AND ${clause}`
}

export function CustomizableChart({ filteredData, filterOptions, query, setQueryAndURL }) {
    const candidate_XValues = filterOptions.filter(x => (x.type === 'array[text]' || x.type === 'text') && (x.values ?? []).length < 1500).map(x => x.columnField)
    const candidate_YValues = ['count'].concat(filterOptions.filter(x => x.type === 'number').map(x => x.columnField))

    const [xAxis, setXAxis] = useStateWithUrlParam('chart_x', candidate_XValues[0])
    const [yAxis, setYAxis] = useStateWithUrlParam('chart_y', candidate_YValues[0])

    const filterOptionsStr = filterOptions.toString()
    useEffect(() => {
        candidate_XValues.includes(xAxis) || setXAxis(candidate_XValues[0]);
        candidate_YValues.includes(yAxis) || setYAxis(candidate_YValues[0]);
    }, [filterOptionsStr])

    const emptyPlaceholder = <div className="empty_placeholder">No data found. Try changing your filters or axes?</div>

    const xSelector = <Select
        className={'version-selector'}
        filterable={false}
        popoverProps={{ minimal: true }}
        items={candidate_XValues}
        activeItem={xAxis}
        itemRenderer={(x, { handleClick, modifiers, index }) =>
            <div
                key={x}
                className={'version-option' + (modifiers.active ? ' active' : '')}
                onClick={handleClick}
                onKeyDown={(e) => e.key === 'Enter' && handleClick.bind(this)}
                role='button'
                tabIndex={index}>
                {x}
            </div>}
        onItemSelect={(i) => setXAxis(i)}>

        <Button text={xAxis} rightIcon="caret-down" />
    </Select>

    const ySelector = <Select
        className={'version-selector'}
        filterable={false}
        popoverProps={{ minimal: true }}
        items={candidate_YValues}
        activeItem={yAxis}
        itemRenderer={(x, { handleClick, modifiers, index }) =>
            <div
                key={x}
                className={'version-option' + (modifiers.active ? ' active' : '')}
                onClick={handleClick}
                onKeyDown={(e) => e.key === 'Enter' && handleClick.bind(this)}
                role='button'
                tabIndex={index}>
                {x}
            </div>}
        onItemSelect={(i) => setYAxis(i)}>

        <Button text={yAxis} rightIcon="caret-down" />
    </Select>

    const filterOpt = filterOptions.filter(x => x.columnField === xAxis)[0]
    // let indices = filterOpt?.values ?? [];
    let indices = [];
    if (filterOpt?.type === 'text') {
        indices = Array.from(new Set(filteredData.filter(x => !!x[xAxis]).map(x => x[xAxis])));
    }
    else if (filterOpt?.type === 'array[text]') {
        indices = Array.from(new Set(filteredData.filter(x => !!x[xAxis]).map(x => x[xAxis]).flat()));
    }

    let data = []
    indices.forEach(index_key => {
        let entries = [];
        if (filterOpt?.type === 'text') {
            entries = filteredData.filter(x => x[xAxis] === index_key)
        }
        else if (filterOpt?.type === 'array[text]') {
            entries = filteredData.filter(x => !!x[xAxis] && x[xAxis].includes(index_key))
        }
        let val = 0;
        if (yAxis === 'count') {
            val = entries.length
        } else {
            val = Math.round(entries.map(x => x[yAxis]).reduce((a, b) => a + b, 0) * 100) / 100.0
        }
        const data_obj = {
            [xAxis]: index_key,
            [yAxis]: val,
            color: COLORS[hash(index_key) % COLORS.length]
        }
        data.push(data_obj)
    })
    data = data.sort((a, b) => b[yAxis] - a[yAxis])

    const onBarClick = ({ indexValue }) => {
        if (filterOpt?.type === 'text') {
            setQueryAndURL(addClause(query, `${xAxis} == "${indexValue}"`))
        } else if (filterOpt?.type === 'array[text]') {
            setQueryAndURL(addClause(query, `${xAxis} includes "${indexValue}"`))
        }
    };

    const chartProps = {
        data,
        keys: [yAxis],
        indexBy: xAxis,
        onClick: onBarClick,
        colors: ({ data }) => data.color,
        groupMode: 'grouped',
        layout: 'vertical',
        margin: { top: 10, right: 100, bottom: data.length >= 10 ? 120 : 50, left: 100 },
        padding: 0.3,
        valueScale: { type: 'linear' },
        indexScale: { type: 'band', round: true },
        enableLabel: data.length < 10,
        labelTextColor: {
            from: 'color',
            modifiers: [
                [
                    'darker',
                    1.6
                ]
            ]
        },
        axisBottom: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: data.length >= 10 ? -30 : 0,
            legendOffset: data.length >= 10 ? 105 : 40,
        },
        axisLeft: {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
        },
    }

    const ChartComponent = data.length > 20 ? ResponsiveBarCanvas : ResponsiveBar;

    return <div className="customizablechart">
        {data.length === 0 && emptyPlaceholder}
        <div className="ySelector">{ySelector}</div>
        <div className="chart-container">
            <ChartComponent {...chartProps} />
        </div>
        <div className="xSelector">{xSelector}</div>
    </div>
}